import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Zap,
  Calendar,
  RefreshCw,
  Lightbulb,
  Filter,
  DollarSign,
  Activity,
  Calendar as CalendarIcon
} from 'lucide-react';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import MetricsDetailModal from '@/components/modals/MetricsDetailModal';
import apiService from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateBR } from '@/utils/dateUtils';

// Interfaces para os dados
interface OverviewData {
  total_boletos: number;
  enviados: number;
  falhas: number;
  pendentes: number;
  fila: number;
  contato_invalido: number;
  baixados: number;
  total_value: number;
  enviados_value: number;
  falhas_value: number;
  pendentes_value: number;
  fila_value: number;
  contato_invalido_value: number;
  baixados_value: number;
  success_rate: number;
  failure_rate: number;
  growth_rate: number;
  period: string;
  start_date: string;
  end_date: string;
  client_id: number | null;
}

interface FailureData {
  failure_reasons: Array<{
    motivo_rejeicao: string;
    count: number;
    total_value: number;
  }>;
  failures_by_lote: Array<{
    lote_id: number;
    client_id: number;
    count: number;
    total_value: number;
    client: {
      id: number;
      trade_name: string;
      system_management: string;
    };
  }>;
  failures_by_client: Array<{
    client_id: number;
    count: number;
    total_value: number;
    client: {
      id: number;
      trade_name: string;
      system_management: string;
    };
  }>;
  total_failures: number;
  total_failure_value: number;
}

interface RealtimeRates {
  current_rate: {
    per_minute: number;
    per_hour: number;
    last_15_minutes: number;
    last_2_hours: number;
  };
  rate_by_minute: Array<{
    minute: string;
    count: number;
  }>;
  rate_by_hour: Array<{
    hour: string;
    count: number;
  }>;
  timestamp: string;
}

interface DailyErrors {
  daily_errors: Array<{
    date: string;
    count: number;
    total_value: number;
  }>;
  daily_errors_by_type: Array<{
    date: string;
    motivo_rejeicao: string;
    count: number;
  }>;
  total_errors: number;
  total_error_value: number;
}

interface Client {
  id: number;
  trade_name: string;
  system_management: string;
  total_disparos?: number;
  enviados?: number;
  falhas?: number;
  pendentes?: number;
  success_rate?: number;
}

interface Insight {
  type: 'alert' | 'warning' | 'success' | 'info';
  severity: 'low' | 'medium' | 'high';
  title: string;
  description: string;
  recommendation: string;
  icon: string;
}

const AdminAnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtros
  const [period, setPeriod] = useState('today');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
  
  // Dados
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [failureData, setFailureData] = useState<FailureData | null>(null);
  const [realtimeRates, setRealtimeRates] = useState<RealtimeRates | null>(null);
  const [dailyErrors, setDailyErrors] = useState<DailyErrors | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);

  // Estado do modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'total' | 'enviados' | 'falhas' | 'pendentes'>('total');
  const [modalTitle, setModalTitle] = useState('');
  const [modalIcon, setModalIcon] = useState<any>(BarChart3);
  const [modalColor, setModalColor] = useState('bg-gray-500');

  // Carregar clientes baseado no período
  const loadClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate.toISOString().split('T')[0]);
        params.append('end_date', customEndDate.toISOString().split('T')[0]);
      } else {
        params.append('period', period);
      }
      
      const response = await apiService.get(`/analytics/disparos/active-clients?${params}`);
      if (response.success && response.data?.clients) {
        setClients(response.data.clients);
        
        // Se o cliente selecionado não está mais na lista, resetar para "all"
        if (selectedClient !== 'all') {
          const clientExists = response.data.clients.some((client: any) => client.id.toString() === selectedClient);
          if (!clientExists) {
            setSelectedClient('all');
          }
        }
      } else {
        // Fallback para carregar todos os clientes se o endpoint falhar
        const fallbackResponse = await apiService.get('/clients');
        if (fallbackResponse.success && Array.isArray(fallbackResponse.data)) {
          setClients(fallbackResponse.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      // Fallback para carregar todos os clientes
      try {
        const fallbackResponse = await apiService.get('/clients');
        if (fallbackResponse.success && Array.isArray(fallbackResponse.data)) {
          setClients(fallbackResponse.data);
        }
      } catch (fallbackError) {
        console.error('Erro no fallback de clientes:', fallbackError);
      }
    }
  }, [period, customStartDate, customEndDate, selectedClient]);

  // Carregar dados do dashboard
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate.toISOString().split('T')[0]);
        params.append('end_date', customEndDate.toISOString().split('T')[0]);
      } else {
        params.append('period', period);
      }
      
      if (selectedClient !== 'all') {
        params.append('client_id', selectedClient);
      }

      const [
        overviewRes,
        failureRes,
        realtimeRes,
        dailyErrorsRes,
        insightsRes
      ] = await Promise.all([
        apiService.get(`/analytics/disparos/overview?${params}`).catch(err => ({ success: false, error: err })),
        apiService.get(`/analytics/disparos/failures?${params}`).catch(err => ({ success: false, error: err })),
        apiService.get(`/analytics/disparos/realtime-rates?${params}`).catch(err => ({ success: false, error: err })),
        apiService.get(`/analytics/disparos/daily-errors?${params}`).catch(err => ({ success: false, error: err })),
        apiService.get(`/analytics/disparos/insights?${params}`).catch(err => ({ success: false, error: err }))
      ]);

      if (overviewRes?.success && overviewRes.data?.overview) {
        setOverview(overviewRes.data.overview);
      }

      if (failureRes?.success && failureRes.data) {
        setFailureData(failureRes.data);
      }

      if (realtimeRes?.success && realtimeRes.data) {
        setRealtimeRates(realtimeRes.data);
      }

      if (dailyErrorsRes?.success && dailyErrorsRes.data) {
        setDailyErrors(dailyErrorsRes.data);
      }

      if (insightsRes?.success && insightsRes.data?.insights) {
        setInsights(Array.isArray(insightsRes.data.insights) ? insightsRes.data.insights : []);
      }

    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [period, selectedClient, customStartDate, customEndDate]);

  // Limpar calendários quando período não for custom
  useEffect(() => {
    if (period !== 'custom') {
      setIsStartCalendarOpen(false);
      setIsEndCalendarOpen(false);
    }
  }, [period]);

  // Auto-refresh a cada 60 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!refreshing) {
        loadDashboardData();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [loadDashboardData, refreshing]);

  // Carregar dados iniciais
  useEffect(() => {
    loadClients();
    loadDashboardData();
  }, [loadClients, loadDashboardData]);

  // Recarregar clientes quando o período mudar
  useEffect(() => {
    loadClients();
  }, [period, customStartDate, customEndDate]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleTestAlert = async () => {
    try {
      const response = await apiService.get('/analytics/disparos/test-alert');
      if (response.success) {
        alert('✅ Alerta de teste enviado com sucesso! Verifique o email: rms.empreendimento@gmail.com');
      }
    } catch (error) {
      console.error('Erro ao testar alerta:', error);
      alert('❌ Erro ao enviar alerta de teste. Verifique os logs.');
    }
  };

  const handleTestSMTP = async () => {
    try {
      const response = await apiService.get('/analytics/disparos/test-smtp');
      if (response.success) {
        alert('✅ Teste SMTP realizado com sucesso! Verifique o email: rms.empreendimento@gmail.com');
      }
    } catch (error) {
      console.error('Erro ao testar SMTP:', error);
      alert('❌ Erro ao testar SMTP. Verifique as configurações no docker-compose.local.yml');
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPeriodLabel = () => {
    if (period === 'custom' && customStartDate && customEndDate) {
      const start = format(customStartDate, 'dd/MM/yyyy');
      const end = format(customEndDate, 'dd/MM/yyyy');
      return `${start} - ${end}`;
    }
    
    const labels: Record<string, string> = {
      'today': 'Hoje',
      '7d': 'Últimos 7 dias',
      '30d': 'Últimos 30 dias',
      'custom': 'Período Personalizado'
    };
    return labels[period] || period;
  };

  const getInsightColor = (type: string) => {
    const colors: Record<string, string> = {
      alert: 'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      success: 'bg-green-50 border-green-200 text-green-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800'
    };
    return colors[type] || colors.info;
  };

  // Função para abrir modal com detalhes
  const openMetricModal = (type: 'total' | 'enviados' | 'falhas' | 'pendentes') => {
    const configs = {
      total: {
        title: 'Total de Boletos',
        icon: BarChart3,
        color: 'bg-purple-500'
      },
      enviados: {
        title: 'Enviados',
        icon: CheckCircle2,
        color: 'bg-green-500'
      },
      falhas: {
        title: 'Falhas',
        icon: XCircle,
        color: 'bg-red-500'
      },
      pendentes: {
        title: 'Pendentes',
        icon: Clock,
        color: 'bg-yellow-500'
      }
    };

    const config = configs[type];
    setModalType(type);
    setModalTitle(config.title);
    setModalIcon(config.icon);
    setModalColor(config.color);
    setModalOpen(true);
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <AtomicLoadingScreen 
          message="Carregando dashboard..." 
          className="h-64"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Título */}
          <div className="py-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              Dashboard Admin - Analytics
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Monitoramento e análise de disparos
            </p>
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-4 pb-4">
            {/* Filtro de Período */}
            <div className="flex items-center gap-2">
              <Label htmlFor="period" className="text-sm font-medium text-gray-700">
                Período:
              </Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="7d">7 dias</SelectItem>
                  <SelectItem value="30d">30 dias</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Período customizado */}
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <Popover open={isStartCalendarOpen} onOpenChange={setIsStartCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-11 justify-start text-left font-normal w-40">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customStartDate ? format(customStartDate, 'dd/MM/yyyy') : 'Data início'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                    <CalendarComponent
                      mode="single"
                      selected={customStartDate}
                      onSelect={(date) => {
                        setCustomStartDate(date);
                        setIsStartCalendarOpen(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-gray-500">até</span>
                <Popover open={isEndCalendarOpen} onOpenChange={setIsEndCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-11 justify-start text-left font-normal w-40">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customEndDate ? format(customEndDate, 'dd/MM/yyyy') : 'Data fim'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                    <CalendarComponent
                      mode="single"
                      selected={customEndDate}
                      onSelect={(date) => {
                        setCustomEndDate(date);
                        setIsEndCalendarOpen(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Filtro de Cliente */}
            <div className="flex items-center gap-2">
              <Label htmlFor="client" className="text-sm font-medium text-gray-700">
                Cliente:
              </Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Clientes</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.trade_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleTestAlert}
              className="flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Testar Alerta
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleTestSMTP}
              className="flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Testar SMTP
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Período atual */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Período: <span className="font-medium">{getPeriodLabel()}</span> • 
            Última atualização: {new Date().toLocaleString('pt-BR')}
          </p>
        </div>

        {/* Insights/Alerts */}
        {insights.length > 0 && (
          <div className="mb-8 space-y-4">
            {insights.map((insight, idx) => (
              <Alert key={idx} className={getInsightColor(insight.type)}>
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{insight.icon}</div>
                  <div className="flex-1">
                    <AlertTitle className="text-lg font-semibold mb-2">
                      {insight.title}
                      <Badge variant="outline" className="ml-2">
                        {insight.severity === 'high' ? '🔴 Alta' : 
                         insight.severity === 'medium' ? '🟡 Média' : '🟢 Baixa'}
                      </Badge>
                    </AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p className="font-medium">{insight.description}</p>
                      <p className="text-sm">
                        💡 <strong>Recomendação:</strong> {insight.recommendation}
                      </p>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        )}

        {/* Cards de Métricas Principais - Quantidade */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Métricas por Quantidade</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total de Boletos */}
            <Card 
              className="border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-purple-300"
              onClick={() => openMetricModal('total')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Total de Boletos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">
                      {formatNumber(overview?.total_boletos || 0)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {(overview?.growth_rate || 0) >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        (overview?.growth_rate || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {Math.abs(overview?.growth_rate || 0).toFixed(1)}%
                      </span>
                      <span className="text-xs text-gray-500">vs período anterior</span>
                    </div>
                  </div>
                  <BarChart3 className="w-12 h-12 text-gray-300" />
                </div>
              </CardContent>
            </Card>

            {/* Enviados */}
            <Card 
              className="border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-green-300"
              onClick={() => openMetricModal('enviados')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Enviados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-green-600">
                      {formatNumber(overview?.enviados || 0)}
                    </div>
                    <div className="mt-2">
                      <Progress value={overview?.success_rate || 0} className="h-2" />
                      <span className="text-sm font-medium text-green-600 mt-1 block">
                        {overview?.success_rate.toFixed(1)}% de sucesso
                      </span>
                    </div>
                  </div>
                  <CheckCircle2 className="w-12 h-12 text-green-200" />
                </div>
              </CardContent>
            </Card>

            {/* Falhas */}
            <Card 
              className="border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-red-300"
              onClick={() => openMetricModal('falhas')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Falhas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-red-600">
                      {formatNumber(overview?.falhas || 0)}
                    </div>
                    <div className="mt-2">
                      <Progress value={overview?.failure_rate || 0} className="h-2 bg-red-100" />
                      <span className="text-sm font-medium text-red-600 mt-1 block">
                        {overview?.failure_rate.toFixed(1)}% de falha
                      </span>
                    </div>
                  </div>
                  <XCircle className="w-12 h-12 text-red-200" />
                </div>
              </CardContent>
            </Card>

            {/* Pendentes */}
            <Card 
              className="border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-yellow-300"
              onClick={() => openMetricModal('pendentes')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-yellow-600">
                      {formatNumber(overview?.pendentes || 0)}
                    </div>
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">
                        + {formatNumber(overview?.fila || 0)} em fila
                      </span>
                      {overview?.contato_invalido && overview.contato_invalido > 0 && (
                        <span className="text-sm text-red-600 ml-2">
                          + {formatNumber(overview.contato_invalido)} contatos inválidos
                        </span>
                      )}
                    </div>
                  </div>
                  <Clock className="w-12 h-12 text-yellow-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Cards de Métricas Principais - Valores (R$) */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Métricas por Valores (R$)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total em Valores */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Valor Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {formatCurrency(overview?.total_value || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Enviados em Valores */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Enviados (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {formatCurrency(overview?.enviados_value || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Falhas em Valores */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Falhas (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">
                  {formatCurrency(overview?.falhas_value || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Pendentes em Valores */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pendentes (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">
                  {formatCurrency(overview?.pendentes_value || 0)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Taxas em Tempo Real */}
        {realtimeRates && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Taxas de Envio em Tempo Real</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Taxa por Minuto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {realtimeRates.current_rate.per_minute.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    últimos 15 minutos
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Taxa por Hora
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {realtimeRates.current_rate.per_hour.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    últimas 2 horas
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Enviados (15min)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {formatNumber(realtimeRates.current_rate.last_15_minutes)}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Enviados (2h)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {formatNumber(realtimeRates.current_rate.last_2_hours)}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Tabs para análises detalhadas */}
        <Tabs defaultValue="failures" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="failures">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Análise de Falhas
            </TabsTrigger>
            <TabsTrigger value="daily-errors">
              <BarChart3 className="w-4 h-4 mr-2" />
              Erros Diários
            </TabsTrigger>
            <TabsTrigger value="realtime">
              <Activity className="w-4 h-4 mr-2" />
              Tempo Real
            </TabsTrigger>
          </TabsList>

          {/* Análise de Falhas */}
          <TabsContent value="failures" className="space-y-6">
            {failureData && (
              <>
                {/* Resumo de Falhas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border border-red-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-red-800">
                        Total de Falhas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-600">
                        {formatNumber(failureData.total_failures)}
                      </div>
                      <div className="text-sm text-red-600 mt-1">
                        {formatCurrency(failureData.total_failure_value)} em valores
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-700">
                        Tipos de Falha
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-gray-900">
                        {failureData.failure_reasons.length}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-700">
                        Lotes com Falhas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-gray-900">
                        {failureData.failures_by_lote.length}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Motivos de Falha */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Motivos de Falha</CardTitle>
                    <CardDescription>
                      Principais causas de falha no período selecionado
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {failureData.failure_reasons.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        <p>🎉 Nenhuma falha registrada neste período!</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {failureData.failure_reasons.slice(0, 10).map((reason, idx) => {
                          const total = failureData.failure_reasons.reduce((sum, r) => sum + Number(r.count), 0);
                          const percentage = (Number(reason.count) / total) * 100;
                          
                          return (
                            <div key={idx} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className="bg-red-50 text-red-700">
                                    #{idx + 1}
                                  </Badge>
                                  <span className="text-sm font-medium">
                                    {reason.motivo_rejeicao || 'Motivo não especificado'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-sm text-gray-600">
                                    {formatNumber(Number(reason.count))} ocorrências
                                  </span>
                                  <span className="text-sm font-bold text-red-600 min-w-[50px] text-right">
                                    {percentage.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <Progress value={percentage} className="h-2" />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Falhas por Lote */}
                <Card>
                  <CardHeader>
                    <CardTitle>Falhas por Lote</CardTitle>
                    <CardDescription>
                      Lotes com maior número de falhas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {failureData.failures_by_lote.slice(0, 10).map((lote, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="bg-red-50 text-red-700">
                              Lote #{lote.lote_id}
                            </Badge>
                            <div>
                              <div className="font-medium">{lote.client.trade_name}</div>
                              <div className="text-sm text-gray-600">{lote.client.system_management}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-red-600">
                              {formatNumber(lote.count)}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatCurrency(lote.total_value)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Erros Diários */}
          <TabsContent value="daily-errors" className="space-y-6">
            {dailyErrors && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Gráfico de Erros por Dia</CardTitle>
                    <CardDescription>
                      Evolução dos erros nos últimos 30 dias
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dailyErrors.daily_errors.length > 0 ? (() => {
                      // Calcular valor máximo real dos dados de erros
                      const maxValue = Math.max(...dailyErrors.daily_errors.map(item => item.count));
                      const yAxisMax = Math.ceil(maxValue * 1.15); // 15% de margem
                      
                      return (
                        <ChartContainer
                          config={{
                            errors: {
                              label: "Erros",
                              color: "#ef4444",
                            },
                          }}
                          className="h-[300px]"
                        >
                          <LineChart data={dailyErrors.daily_errors.map(item => ({
                            date: formatDateBR(item.date),
                            errors: item.count,
                            value: item.total_value
                          }))}>
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 12 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis 
                              tick={{ fontSize: 12 }}
                              domain={[0, yAxisMax]}
                              allowDataOverflow={false}
                            />
                          <ChartTooltip 
                            content={
                              <ChartTooltipContent 
                                formatter={(value, name) => [
                                  name === 'errors' ? `${value} erros` : formatCurrency(Number(value)),
                                  name === 'errors' ? 'Quantidade' : 'Valor (R$)'
                                ]}
                                labelFormatter={(label) => `Data: ${label}`}
                              />
                            }
                          />
                          <Line 
                            type="monotone" 
                            dataKey="errors" 
                            stroke="#ef4444" 
                            strokeWidth={2}
                            dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ChartContainer>
                      );
                    })() : (
                      <div className="text-center py-8">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                        <p className="text-gray-600">Nenhum erro registrado neste período</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Resumo de Erros Diários</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          {formatNumber(dailyErrors.total_errors)}
                        </div>
                        <div className="text-sm text-gray-600">Total de Erros</div>
                      </div>
                      <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          {formatCurrency(dailyErrors.total_error_value)}
                        </div>
                        <div className="text-sm text-gray-600">Valor em Erros</div>
                      </div>
                      <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {dailyErrors.daily_errors.length}
                        </div>
                        <div className="text-sm text-gray-600">Dias com Erros</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Tempo Real */}
          <TabsContent value="realtime" className="space-y-6">
            {realtimeRates && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Taxa de Envio por Minuto</CardTitle>
                    <CardDescription>
                      Últimos 60 minutos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {realtimeRates.rate_by_minute.length > 0 ? (() => {
                      // Calcular valor máximo real dos dados
                      const maxValue = Math.max(...realtimeRates.rate_by_minute.map(item => item.count));
                      const yAxisMax = Math.ceil(maxValue * 1.15); // 15% de margem
                      
                      return (
                        <ChartContainer
                          config={{
                            sent: {
                              label: "Enviados",
                              color: "#22c55e",
                            },
                          }}
                          className="h-[300px]"
                        >
                          <BarChart data={realtimeRates.rate_by_minute.map(item => ({
                            time: new Date(item.minute).toLocaleTimeString('pt-BR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            }),
                            sent: item.count
                          }))}>
                            <XAxis 
                              dataKey="time" 
                              tick={{ fontSize: 12 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis 
                              tick={{ fontSize: 12 }}
                              domain={[0, yAxisMax]}
                              allowDataOverflow={false}
                            />
                          <ChartTooltip 
                            content={
                              <ChartTooltipContent 
                                formatter={(value) => [`${value} boletos`, 'Enviados']}
                                labelFormatter={(label) => `Hora: ${label}`}
                              />
                            }
                          />
                          <Bar dataKey="sent" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ChartContainer>
                      );
                    })() : (
                      <div className="text-center py-8">
                        <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">Nenhum dado disponível</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Taxa de Envio por Hora</CardTitle>
                    <CardDescription>
                      Últimas 24 horas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {realtimeRates.rate_by_hour.length > 0 ? (() => {
                      // Calcular valor máximo real dos dados
                      const maxValue = Math.max(...realtimeRates.rate_by_hour.map(item => item.count));
                      const yAxisMax = Math.ceil(maxValue * 1.15); // 15% de margem
                      
                      return (
                        <ChartContainer
                          config={{
                            sent: {
                              label: "Enviados",
                              color: "#3b82f6",
                            },
                          }}
                          className="h-[300px]"
                        >
                          <LineChart data={realtimeRates.rate_by_hour.map(item => ({
                            hour: new Date(item.hour).toLocaleTimeString('pt-BR', { 
                              hour: '2-digit'
                            }),
                            sent: item.count
                          }))}>
                            <XAxis 
                              dataKey="hour" 
                              tick={{ fontSize: 12 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis 
                              tick={{ fontSize: 12 }}
                              domain={[0, yAxisMax]}
                              allowDataOverflow={false}
                            />
                          <ChartTooltip 
                            content={
                              <ChartTooltipContent 
                                formatter={(value) => [`${value} boletos`, 'Enviados']}
                                labelFormatter={(label) => `Hora: ${label}`}
                              />
                            }
                          />
                          <Line 
                            type="monotone" 
                            dataKey="sent" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ChartContainer>
                      );
                    })() : (
                      <div className="text-center py-8">
                        <Zap className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">Nenhum dado disponível</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Detalhes das Métricas */}
      <MetricsDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        metricType={modalType}
        title={modalTitle}
        icon={modalIcon}
        color={modalColor}
        clientId={selectedClient !== 'all' ? parseInt(selectedClient) : undefined}
        period={period}
        customStartDate={customStartDate ? customStartDate.toISOString().split('T')[0] : undefined}
        customEndDate={customEndDate ? customEndDate.toISOString().split('T')[0] : undefined}
      />
    </div>
  );
};

export default AdminAnalyticsDashboard;
